// src/products/dataroom/components/Modals/AddTeamModal.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Users, Building2 } from 'lucide-react';
import { supabase } from '../../../../config';
import partyTeamService from '../../services/partyTeamService';

const AddTeamModal = ({ isOpen, onClose, onComplete }) => {
  const [projects, setProjects] = useState([]);
  const [parties, setParties] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedParty, setSelectedParty] = useState('');
  const [teams, setTeams] = useState([{ name: '', code: '', description: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadProjects();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedProject) {
      loadParties(selectedProject);
    }
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('dataroom_projects')
        .select('id, project_name')
        .order('project_name');

      if (error) throw error;
      setProjects(data || []);
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const loadParties = async (projectId) => {
    try {
      const { data, error } = await supabase
        .from('dataroom_parties')
        .select('id, name, code, color')
        .eq('project_id', projectId)
        .order('name');

      if (error) throw error;
      setParties(data || []);
      setSelectedParty('');
    } catch (err) {
      console.error('Error loading parties:', err);
    }
  };

  const addTeamRow = () => {
    setTeams([...teams, { name: '', code: '', description: '' }]);
  };

  const removeTeamRow = (index) => {
    if (teams.length > 1) {
      setTeams(teams.filter((_, i) => i !== index));
    }
  };

  const updateTeam = (index, field, value) => {
    const updated = [...teams];
    updated[index][field] = value;
    setTeams(updated);
  };

  const handleSubmit = async () => {
    if (!selectedProject) {
      setError('Please select a dataroom project');
      return;
    }

    if (!selectedParty) {
      setError('Please select a party');
      return;
    }

    const validTeams = teams.filter(t => t.name.trim() && t.code.trim());
    if (validTeams.length === 0) {
      setError('Please add at least one team with name and code');
      return;
    }

    // Check for duplicate codes
    const codes = validTeams.map(t => t.code.toUpperCase());
    const duplicateCodes = codes.filter((c, i) => codes.indexOf(c) !== i);
    if (duplicateCodes.length > 0) {
      setError(`Duplicate code(s): ${duplicateCodes.join(', ')}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const savedTeams = [];

      for (const team of validTeams) {
        const result = await partyTeamService.createTeam(parseInt(selectedParty), {
          name: team.name.trim(),
          code: team.code.toUpperCase().trim(),
          description: team.description.trim() || 'Custom team',
          team_type: 'general'
        });

        if (result.error) {
          console.error(`Failed to create team "${team.name}":`, result.error);
          continue;
        }

        console.log(`✓ Created team: ${team.name}`);
        savedTeams.push(result.data);
      }

      if (savedTeams.length > 0) {
        onComplete && onComplete(savedTeams);
        handleClose();
      } else {
        setError('Failed to create teams');
      }
    } catch (err) {
      console.error('Error creating teams:', err);
      setError(err.message || 'Failed to create teams');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedProject('');
    setSelectedParty('');
    setTeams([{ name: '', code: '', description: '' }]);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const selectedPartyData = parties.find(p => p.id === parseInt(selectedParty));

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <Users className="w-6 h-6 text-white mr-3" />
            <h2 className="text-xl font-bold text-white">Add Teams</h2>
          </div>
          <button onClick={handleClose} className="text-white hover:text-green-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Selection Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Project Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 className="w-4 h-4 inline mr-1" />
                Dataroom Project *
              </label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Select Project...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.project_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Party Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 className="w-4 h-4 inline mr-1" />
                Party *
              </label>
              <select
                value={selectedParty}
                onChange={(e) => setSelectedParty(e.target.value)}
                disabled={!selectedProject}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select Party...</option>
                {parties.map(party => (
                  <option key={party.id} value={party.id}>
                    {party.code} - {party.name}
                  </option>
                ))}
              </select>
              {selectedPartyData && (
                <div className="mt-2 flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: selectedPartyData.color }}
                  />
                  <span className="text-xs text-gray-600">{selectedPartyData.name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Teams Section */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Teams to Add to {selectedPartyData?.name || 'Selected Party'}
              </label>
              <button
                onClick={addTeamRow}
                disabled={!selectedParty}
                className="text-sm text-green-600 hover:text-green-700 flex items-center disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Another Team
              </button>
            </div>

            <div className="space-y-3 max-h-60 overflow-y-auto">
              {teams.map((team, index) => (
                <div key={index} className="flex gap-2 items-start bg-gray-50 p-3 rounded-lg">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                    <input
                      type="text"
                      placeholder="Code (e.g., T01)"
                      value={team.code}
                      onChange={(e) => updateTeam(index, 'code', e.target.value.toUpperCase())}
                      maxLength={10}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <input
                      type="text"
                      placeholder="Team Name *"
                      value={team.name}
                      onChange={(e) => updateTeam(index, 'name', e.target.value)}
                      maxLength={50}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={team.description}
                      onChange={(e) => updateTeam(index, 'description', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <button
                    onClick={() => removeTeamRow(index)}
                    disabled={teams.length === 1}
                    className="p-2 text-red-600 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              <strong>Note:</strong> Teams are permission groups within a party. 
              After creating teams, you can set file permissions for each team.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !selectedProject || !selectedParty}
            className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating Teams...
              </>
            ) : (
              <>
                <Users className="w-4 h-4 mr-2" />
                Create {teams.filter(t => t.name.trim() && t.code.trim()).length} Team(s)
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddTeamModal;









